import React, { useState, useMemo, useEffect } from 'react';
import {
  FormControl,
  FormLabel,
  Input,
  Textarea,
  FormHelperText,
  FormErrorMessage,
  Flex,
  Box,
} from '@chakra-ui/react';
import ButtonComponent from './ButtonComponent';

const ERROR_COLOR = '#CD0000';

const FormComponent = ({ section, formData, readOnly, updateFormData, onSubmit, isTouchDevice, isMouseDevice}) => {
  const { title, variant, required = [], items = [] } = section;
  const buttonPaddingTop = section.buttonPaddingTop !== undefined ? section.buttonPaddingTop : 4;
  const buttonPaddingBottom = section.buttonPaddingBottom !== undefined ? section.buttonPaddingBottom : 0;
  const buttonMarginTop = section.buttonMarginTop !== undefined ? section.buttonMarginTop : 'auto';

  useEffect(() => {
    items.forEach((item) => {
      if (!formData[item.id]) {
        updateFormData(item.id, item.value || "");
      }
    });
  }, [items, formData, updateFormData]);

  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);

  const setFieldError = (id, message) => {
    setErrors((prev) => {
      const next = { ...prev };
      if (message) {
        next[id] = message;
      } else {
        delete next[id];
      }
      return next;
    });
  };

  const getFieldLabelText = (item) => item?.label || item?.helperText || 'This field';

  const isFieldRequired = (item) => required.includes(item?.id) || item?.required === true;

  const getCurrentFieldValue = (item) => {
    const formValue = formData[item?.id]?.value;
    if (formValue !== undefined && formValue !== null) {
      return formValue;
    }
    return item?.value ?? '';
  };

  const applyDobMask = (rawValue = "") => {
    const digits = String(rawValue).replace(/\D/g, '').slice(0, 8);
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  };

  const normalizeInputValue = (item, rawValue) => {
    const stringValue = String(rawValue ?? '');

    if (item?.maskType === 'dob') {
      return applyDobMask(stringValue);
    }

    if (item?.numericOnly) {
      const maxDigits = Number.isInteger(item.maxDigits) ? item.maxDigits : null;
      const digits = stringValue.replace(/\D/g, '');
      return maxDigits ? digits.slice(0, maxDigits) : digits;
    }

    return stringValue;
  };

  const getDobValidationError = (value = '', item = {}) => {
    const match = String(value).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!match) {
      return item?.formatErrorMessage || 'Enter DOB in MM/DD/YYYY format.';
    }

    const month = Number(match[1]);
    const day = Number(match[2]);
    const year = Number(match[3]);

    if (month < 1 || month > 12 || day < 1) {
      return item?.invalidDateErrorMessage || 'Enter a valid date of birth.';
    }

    const now = new Date();
    const currentYear = now.getFullYear();
    if (year < 1900 || year > currentYear) {
      return item?.yearRangeErrorMessage || `Enter a birth year between 1900 and ${currentYear}.`;
    }

    const daysInMonth = new Date(year, month, 0).getDate();
    if (day > daysInMonth) {
      return item?.invalidDateErrorMessage || 'Enter a valid date of birth.';
    }

    return '';
  };

  const getFieldValidationError = (item, value) => {
    const stringValue = String(value ?? '').trim();
    const labelText = getFieldLabelText(item);
    const minValue = item?.min !== undefined ? Number(item.min) : null;
    const maxValue = item?.max !== undefined ? Number(item.max) : null;

    if (isFieldRequired(item) && !stringValue) {
      return item?.requiredErrorMessage || `${labelText} is required.`;
    }

    if (!stringValue) {
      return '';
    }

    if (item?.maskType === 'dob') {
      return getDobValidationError(stringValue, item);
    }

    if (item?.numericOnly) {
      if (!/^\d+$/.test(stringValue)) {
        return item?.numberErrorMessage || `${labelText} must be a number.`;
      }

      const numericValue = Number(stringValue);
      if (Number.isFinite(minValue) && numericValue < minValue) {
        return item?.minErrorMessage || `${labelText} must be at least ${minValue}.`;
      }

      if (Number.isFinite(maxValue) && numericValue > maxValue) {
        return item?.maxErrorMessage || `${labelText} must be at most ${maxValue}.`;
      }
    }

    return '';
  };

  const handleInputChange = (item, value) => {
    const normalizedValue = normalizeInputValue(item, value);
    updateFormData(item.id, normalizedValue);

    if (item?.numericOnly && /[^\d]/.test(String(value ?? ''))) {
      setFieldError(
        item.id,
        item?.numberErrorMessage || `${getFieldLabelText(item)} must be a number.`
      );
      return;
    }

    const validationError = getFieldValidationError(item, normalizedValue);
    setFieldError(item.id, validationError);
  };

  const handleBlur = (item) => {
    const id = item.id;
    const value = getCurrentFieldValue(item);
    const validationError = getFieldValidationError(item, value);
    setFieldError(id, validationError);
  };

  const validateForm = () => {
    const newErrors = {};
    items.forEach((item) => {
      const value = getCurrentFieldValue(item);
      const validationError = getFieldValidationError(item, value);
      if (validationError) {
        newErrors[item.id] = validationError;
      }
    });

    required.forEach((field) => {
      if (newErrors[field]) return;
      const item = items.find((candidate) => candidate.id === field);
      const value = item ? getCurrentFieldValue(item) : formData[field]?.value;
      if (!value || !String(value).trim()) {
        newErrors[field] = 'This field is required.';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFormSubmit = (btn) => {
    setIsSubmitted(true);
    if (validateForm()) {
      onSubmit(btn);
    }
  };

  const isFormValid = useMemo(() => {
    return items.every((item) => {
      const value = getCurrentFieldValue(item);
      return !getFieldValidationError(item, value);
    });
  }, [formData, required, items]);

  useEffect(() => {
    if (!items.length) return;

    // Keep visible errors in sync for prefilled invalid values on first paint.
    // Intentionally skip empty fields here so untouched required fields do not
    // immediately show errors before user interaction or submit.
    setErrors((prev) => {
      const next = { ...prev };
      let changed = false;

      items.forEach((item) => {
        const id = item?.id;
        if (!id) return;

        const value = getCurrentFieldValue(item);
        const hasContent = String(value ?? '').trim().length > 0;
        if (!hasContent) return;

        const validationError = getFieldValidationError(item, value);
        if (validationError) {
          if (next[id] !== validationError) {
            next[id] = validationError;
            changed = true;
          }
        } else if (next[id]) {
          delete next[id];
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [items, formData]);

  const renderField = (item, itemIndex) => {
    const { type, label, name, id, placeholder, value, variant, rows } = item;

    const errorMessage = errors[id]; // Show error for invalid fields
    const isError = !!errorMessage;
    const isRequiredField = isFieldRequired(item);
    const normalizedLabel =
      isRequiredField && typeof label === 'string'
        ? label.replace(/\s*\*+\s*$/, '')
        : label;
    const hasVisibleLabel = typeof normalizedLabel === 'string'
      ? normalizedLabel.trim().length > 0
      : !!normalizedLabel;

    // If a label represents multiple adjacent fields and the following field(s) are unlabeled,
    // bubble unlabeled field errors up to the visible label (e.g., Height + Inches).
    let linkedUnlabeledError = false;
    if (hasVisibleLabel) {
      for (let i = itemIndex + 1; i < items.length; i += 1) {
        const nextLabel = items[i]?.label;
        const hasNextVisibleLabel = typeof nextLabel === 'string'
          ? nextLabel.trim().length > 0
          : !!nextLabel;
        if (hasNextVisibleLabel) {
          break;
        }
        if (errors[items[i]?.id]) {
          linkedUnlabeledError = true;
          break;
        }
      }
    }

    const linkedSharedLabelError = hasVisibleLabel && items.some((candidate) => {
      if (!candidate?.id || !candidate?.sharesLabelWith) return false;
      return candidate.sharesLabelWith === id && !!errors[candidate.id];
    });

    const shouldHighlightLabel = isError || linkedUnlabeledError || linkedSharedLabelError;

    switch (type) {
      case 'input':
        return (
          <FormControl
            isInvalid={isError}
            key={id}
            mb={4}
            isRequired={isRequiredField && hasVisibleLabel}
            sx={{
              '.chakra-form__required-indicator': {
                color: shouldHighlightLabel ? ERROR_COLOR : undefined,
              },
            }}
          >
            <FormLabel
              htmlFor={id}
              color={shouldHighlightLabel ? ERROR_COLOR : undefined}
              style={{
                fontFamily: `var(--${variant}-font-family, Roboto)`,
                fontSize: `var(--${variant}-font-size, 16px)`,
                fontWeight: `var(--${variant}-font-weight, 600)`,
                color: shouldHighlightLabel ? ERROR_COLOR : `var(--${variant}-color, var(--color-dusky-teal))`,
              }}
            >
              {normalizedLabel}
            </FormLabel>
            <Input
              id={id}
              name={name}
              placeholder={placeholder}
              value={formData[id]?.value || ""}
              onChange={(e) => handleInputChange(item, e.target.value)}
              onBlur={() => handleBlur(item)}
              inputMode={item.inputMode || (item.maskType === 'dob' || item.numericOnly ? 'numeric' : undefined)}
              pattern={item.pattern || (item.numericOnly ? '[0-9]*' : undefined)}
              maxLength={
                Number.isInteger(item.maxLength)
                  ? item.maxLength
                  : (item.maskType === 'dob' ? 10 : undefined)
              }
              aria-describedby={isError ? `${id}-error` : undefined}
              style={{
                fontFamily: `var(--${variant}-font-family, Roboto)`,
                fontSize: `var(--${variant}-font-size, 16px)`,
                padding: `var(--${variant}-padding, 12px)`,
                border: `1px solid ${isError ? ERROR_COLOR : 'var(--sutter-sync-purple)'}`,
                borderRadius: `var(--${variant}-border-radius, 6px)`,
                backgroundColor: `var(--${variant}-background-color, #fff)`,
                color: `var(--${variant}-color, var(--color-dusky-teal))`,
              }}
              _focus={{
                borderColor: isError ? ERROR_COLOR : `var(--sutter-sync-purple)`,
                boxShadow: 'none',
                outline: 'none',
              }}
              _focusVisible={{
                borderColor: isError ? ERROR_COLOR : `var(--sutter-sync-purple)`,
                boxShadow: 'none',
                outline: 'none',
              }}
              _invalid={{
                borderColor: ERROR_COLOR,
                boxShadow: 'none',
              }}
            />
            {item.helperText && !isError ? (
              <FormHelperText>
                {item.helperText}
              </FormHelperText>
            ) : null}
            {isError && (
              <FormErrorMessage id={`${id}-error`}>
                <Box
                  as="span"
                  display="inline-flex"
                  alignItems="center"
                  justifyContent="center"
                  width="1em"
                  height="1em"
                  minWidth="1em"
                  borderRadius="full"
                  backgroundColor={ERROR_COLOR}
                  color="white"
                  fontWeight="700"
                  lineHeight="1"
                  marginRight="8px"
                  marginTop="1px"
                >
                  !
                </Box>
                {errorMessage}
              </FormErrorMessage>
            )}
          </FormControl>
        );

      case 'textarea':
        return (
          <FormControl
            isInvalid={isError}
            key={id}
            mb={4}
            isRequired={isRequiredField && hasVisibleLabel}
            sx={{
              '.chakra-form__required-indicator': {
                color: shouldHighlightLabel ? ERROR_COLOR : undefined,
              },
            }}
          >
            <FormLabel
              htmlFor={id}
              color={shouldHighlightLabel ? ERROR_COLOR : undefined}
              style={{
                fontFamily: `var(--${variant}-font-family, Roboto)`,
                fontSize: `var(--${variant}-font-size, 16px)`,
                fontWeight: `var(--${variant}-font-weight, 600)`,
                color: shouldHighlightLabel ? ERROR_COLOR : `var(--${variant}-color, var(--color-dusky-teal))`,
              }}
            >
              {normalizedLabel}
            </FormLabel>
            <Textarea
              id={id}
              name={name}
              placeholder={placeholder}
              value={formData[id]?.value || value || ''}
              onChange={(e) => handleInputChange(item, e.target.value)}
              onBlur={() => handleBlur(item)}
              rows={rows || 4}
              aria-describedby={isError ? `${id}-error` : undefined}
              style={{
                fontSize: `var(--${variant}-font-size, 16px)`,
                padding: `var(--${variant}-padding, 12px)`,
                border: `1px solid ${isError ? ERROR_COLOR : `var(--${variant}-border-color, var(--sutter-sync-purple))`}`,
                borderRadius: `var(--${variant}-border-radius, 6px)`,
                backgroundColor: `var(--${variant}-background-color, #fff)`,
                color: `var(--${variant}-color, var(--text-primary-color))`,
              }}
              _focus={{
                borderColor: isError ? ERROR_COLOR : `var(--${variant}-focus-border-color, var(--sutter-sync-purple))`,
                boxShadow: 'none',
                outline: 'none',
              }}
              _focusVisible={{
                borderColor: isError ? ERROR_COLOR : `var(--${variant}-focus-border-color, var(--sutter-sync-purple))`,
                boxShadow: 'none',
                outline: 'none',
              }}
              _invalid={{
                borderColor: ERROR_COLOR,
                boxShadow: 'none',
              }}
            />
            {isError && (
              <FormErrorMessage id={`${id}-error`}>
                <Box
                  as="span"
                  display="inline-flex"
                  alignItems="center"
                  justifyContent="center"
                  width="1em"
                  height="1em"
                  minWidth="1em"
                  borderRadius="full"
                  backgroundColor={ERROR_COLOR}
                  color="white"
                  fontWeight="700"
                  lineHeight="1"
                  marginRight="8px"
                  marginTop="1px"
                >
                  !
                </Box>
                {errorMessage}
              </FormErrorMessage>
            )}
          </FormControl>
        );

      default:
        console.warn(`Unsupported field type: ${type}`);
        return null;
    }
  };

  return (
    <Flex
      direction="column"
      height="100%"
    >
      <Box flex="1" display="flex" flexDirection="column">
        {title && (
          <h2
            style={{
              fontFamily: `var(--${variant}-font-family, Roboto)`,
              fontSize: `var(--${variant}-font-size, 20px)`,
              fontWeight: `var(--${variant}-font-weight, 700)`,
              color: `var(--${variant}-color, var(--color-dusky-teal))`,
              marginBottom: '16px',
            }}
          >
            {title}
          </h2>
        )}
        {items.map((item, itemIndex) => renderField(item, itemIndex))}
      </Box>

      {!readOnly && section.button?.length > 0 && (
        <Box mt={buttonMarginTop} pt={buttonPaddingTop} pb={buttonPaddingBottom}>
          {section.button.map((btn, index) => {
            return (
              <ButtonComponent
                key={index}
                label={btn.label || 'Submit??'}
                variant={btn.variant || 'solid'}
                isDisabled={
                  // Only disable submit/next buttons when form is invalid
                  // Exception: "No Thanks" buttons should always be enabled (optional actions)
                  (btn.action === 'submit' || btn.action === 'next')
                    ? !isFormValid && !(btn.label?.toLowerCase().includes('no thanks'))
                    : false
                }
                ariaLabel={btn.ariaLabel || ''}
                tooltip={btn.tooltip || ''}
                onClick={() => {
                  if (btn.action === 'back') {
                    onSubmit(btn);
                  } else {
                    handleFormSubmit(btn);
                  }
                }}
                textDecoration={btn.textDecoration}
                isTouchDevice={isTouchDevice}
                isMouseDevice={isMouseDevice}
              />
            );
          })}
        </Box>
      )}
    </Flex>
  );
};

export default FormComponent;
